import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output} from '@angular/core';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'ee-expansion-panel',
  template: `
    <div
      class="ee__expansion-panel"
      [ngClass]="{
        'full-height': fullHeight,
        'full-width': fullWidth,
        minimal: theme === 'minimal',
        flat: theme === 'flat',
        'pb-4': bottomPadding,
        expanded: expanded,
        collapsed: !expanded
      }">
      <div class="ee__expansion-panel-header">
        <div class="ee__expansion-panel-header-text">
          {{ header }}
          @if (required) {
            <span class="required">*</span>
          }
        </div>
        <div class="ee__expansion-panel-header-actions">
          <ng-content select="[ee-expansion-panel-header-actions]"></ng-content>
        </div>
        @if (collapsible) {
          <button class="ee__expansion-panel-expander" type="button" mat-icon-button (click)="changeExpandedState()">
            <mat-icon fontSet="fa" [fontIcon]="expanded ? 'fa-chevron-down' : 'fa-chevron-up'"></mat-icon>
          </button>
        }
      </div>
      <div
        [hidden]="!expanded"
        [class]="'ee__expansion-panel-body ' + additionalContentClasses"
        [ngClass]="{ 'h-full': fullHeight }">
        <ng-content></ng-content>
      </div>
    </div>
  `,
  styles: [
    `
      .ee__expansion-panel {
        box-shadow: var(--modern-box-shadow);
        display: flex;
        flex-direction: column;
        flex: 1;
        background-color: var(--color-surface);

        &.full-height {
          height: 100%;
        }

        &.full-width {
          width: 100%;
        }

        &.minimal .ee__expansion-panel-header {
          border-bottom: none;
          padding-bottom: 0;

          .ee__expansion-panel-header-text {
            text-transform: none;
            font-weight: 600;
            color: var(--color-on-surface);
          }
        }

        &.flat {
          box-shadow: none;
          border-radius: 16px;
          border: 1px solid var(--color-outline);

          &.collapsed .ee__expansion-panel-header {
            border-bottom: none;
          }

          .ee__expansion-panel-header {
            .ee__expansion-panel-header-text {
              text-transform: none;
              font-weight: 600;
            }
          }
        }

        .ee__expansion-panel-header {
          font-size: 1rem;
          padding: 1rem;
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid var(--color-outline);
          max-height: 57px;

          .ee__expansion-panel-header-actions {
            display: flex;
            flex-direction: row;
            justify-content: flex-end;
            align-items: center;
          }

          .ee__expansion-panel-header-text {
            flex: 1;
            font-weight: 500;
            text-transform: uppercase;
            color: var(--color-on-surface);

            .required {
              color: red;
            }

            .disable-msg {
              color: red;
              margin-left: 10px;
              font-size: 12px;
              text-transform: none;
            }
          }

          .ee__expansion-panel-expander {
            height: 24px;
            width: 24px;
            padding: 0;
            color: var(--color-on-surface-variant);

            .mat-icon {
              line-height: 0.875rem;
              height: 1rem;
              width: 1rem;
              font-size: 1rem;
            }
          }
        }
        .ee__expansion-panel-body {
          padding: 1rem;
          height: auto;
          overflow: hidden;
          flex: 1;
        }
      }
    `
  ],
  standalone: false
})
export class ExpansionPanelComponent {
  @Input() theme: 'standard' | 'minimal' | 'flat' = 'standard';

  @Input() header: string;

  @Input() required = false;

  @Input() expanded = true;

  @Input() fullHeight = false;

  @Input() fullWidth = false;

  @Input() collapsible = true;

  @Input() additionalContentClasses: string;

  @Input() bottomPadding = false;

  @Output() expandedStateChanged = new EventEmitter<boolean>();

  public constructor(private _change: ChangeDetectorRef) {}

  changeExpandedState() {
    this.expanded = !this.expanded;
    this.expandedStateChanged.emit(this.expanded);
  }

  updateExpandedState(state: boolean): void {
    this.expanded = state;
    this._change.markForCheck();
  }
}
